import { ToasterToast } from '@ais-ui/ui-core';
import { capitalizeFirstLetter } from './capitalizeFirstLetter';
import { Messages } from '@ais-ui/locale-management';

type MutationType = 'create' | 'edit' | 'delete' | 'change' | 'send';

export const readErrorBody = async (error: any) => {
  try {
    // First check if ApiClientWrapper already parsed the body
    if (error.response?.parsedBody) {
      return error.response.parsedBody;
    }

    // Fallback to reading the raw response body (for cases where ApiClientWrapper didn't parse it)
    const responseBody = error.response?.body;

    if (responseBody instanceof ReadableStream) {
      const reader = responseBody.getReader();
      const { value } = await reader.read();
      const text = new TextDecoder().decode(value);
      const parsed = JSON.parse(text);
      return parsed;
    }

    return null;
  } catch (e) {
    console.error('Error reading error body:', e);
    return null;
  }
};

const mutationPastTense: Record<MutationType, string> = {
  create: 'created',
  edit: 'edited',
  delete: 'deleted',
  change: 'changed',
  send: 'sent',
};

export const mutateCallbacks = (
  toast: (props: Omit<ToasterToast, 'id'>) => {
    id: string;
    dismiss: () => void;
    update: (props: ToasterToast) => void;
  },
  messages: Messages,
  mutation: MutationType,
  entity: string,
  setIsSubmitting: (isSubmitting: boolean) => void,
  setShowDialog?: (showDialog: boolean) => void,
  customErrorMessages?: { [key: number]: string }
) => {
  const successClassName = `${mutation}-${entity}-success-toast`;
  const errorClassName = `${mutation}-${entity}-error-toast`;

  const successMessage = capitalizeFirstLetter(
    messages.genericToastGenericSuccessMessage
      .replace('{mutationPastTense}', mutationPastTense[mutation])
      .replace('{entity}', entity)
  );

  const duplicatedDataErrorMessage =
    messages.genericToastGenericErrorDuplicatedDataMessage.replace(
      '{entity}',
      entity
    );

  const permissionErrorMessage = messages.genericToastPermissionErrorMessage
    .replace('{mutation}', mutation)
    .replace('{entity}', entity);

  const defaultErrorMessage = messages.genericToastGenericErrorMessage
    .replace('{mutation}', mutation)
    .replace('{entity}', entity);

  return {
    onSuccess: () => {
      toast({
        className: successClassName,
        title: messages.genericToastTitleSuccess,
        description: successMessage,
      });
      if (setShowDialog) {
        setShowDialog(false);
      }
    },
    onError: (error: any) => {
      const statusCode = error.response?.status;
      const customErrorMessage = customErrorMessages?.[statusCode];
      if (statusCode === 403) {
        toast({
          className: errorClassName,
          title: messages.genericToastTitleForbidden,
          description: permissionErrorMessage,
          variant: 'destructive',
        });
      } else if (statusCode === 409) {
        toast({
          className: errorClassName,
          title: messages.genericToastTitleError,
          description: duplicatedDataErrorMessage,
          variant: 'destructive',
        });
      } else {
        toast({
          className: errorClassName,
          title: messages.genericToastTitleError,
          description: customErrorMessage || defaultErrorMessage,
          variant: 'destructive',
        });
      }
    },
    onSettled: () => {
      setIsSubmitting(false);
    },
  };
};

export const shiplistCallbacks = (
  toast: (props: Omit<ToasterToast, 'id'>) => {
    id: string;
    dismiss: () => void;
    update: (props: ToasterToast) => void;
  },
  messages: Messages,
  mutation: MutationType,
  setIsSubmitting: (isSubmitting: boolean) => void,
  setShowDialog?: (showDialog: boolean) => void,
  setInvalidIndices?: React.Dispatch<
    React.SetStateAction<{ imo: number[]; mmsi: number[] }>
  >,
  setValidationErrorMessages?: React.Dispatch<
    React.SetStateAction<Record<number, string>>
  >
) => {
  const callBacks = mutateCallbacks(
    toast,
    messages,
    mutation,
    'shiplist',
    setIsSubmitting,
    setShowDialog
  );

  const extendedOnError = async (error: any) => {
    const statusCode = error.response?.status;
    const errorMessage = error.response?.message;

    if (statusCode === 400) {
      if (errorMessage?.includes('Invalid MMSI number, must be 9 digits')) {
        toast({
          title: messages.genericToastTitleError,
          description: 'Invalid MMSI number, must be 9 digits',
          variant: 'destructive',
        });
        return;
      }
    }

    if (statusCode === 409) {
      try {
        const errorData = await readErrorBody(error);

        if (errorData?.details?.validation_errors) {
          const validationErrors = errorData.details.validation_errors;
          const errorDescriptions = validationErrors.map(
            (validationError: any) => {
              const { reason, row_numbers } = validationError;
              const rowText =
                row_numbers?.length > 1
                  ? `rows ${row_numbers.join(', ')}`
                  : `row ${row_numbers[0]}`;
              return `${reason} (${rowText})`;
            }
          );

          toast({
            title: messages.genericToastTitleError,
            description: errorDescriptions.join('\n'),
            variant: 'destructive',
          });

          // Highlight the problematic rows and set tooltip messages
          if (setInvalidIndices && setValidationErrorMessages) {
            const invalidMmsiIndices: number[] = [];
            const invalidImoIndices: number[] = [];
            const errorMessages: Record<number, string> = {};

            validationErrors.forEach((validationError: any) => {
              const { reason, row_numbers } = validationError;

              // Convert from 1-based (API) to 0-based (UI) row numbers
              const zeroBasedRowNumbers = row_numbers.map(
                (rowNum: number) => rowNum - 1
              );

              // Store error message for each affected row
              zeroBasedRowNumbers.forEach((rowIndex: number) => {
                errorMessages[rowIndex] = reason;
              });

              if (reason.toLowerCase().includes('mmsi')) {
                invalidMmsiIndices.push(...zeroBasedRowNumbers);
              } else if (reason.toLowerCase().includes('imo')) {
                invalidImoIndices.push(...zeroBasedRowNumbers);
              } else {
                // For other validation errors, treat as MMSI errors for now
                invalidMmsiIndices.push(...zeroBasedRowNumbers);
              }
            });

            setInvalidIndices({
              mmsi: [...new Set(invalidMmsiIndices)], // Remove duplicates
              imo: [...new Set(invalidImoIndices)], // Remove duplicates
            });

            setValidationErrorMessages(errorMessages);
          }

          return;
        }
      } catch (parseError) {
        console.error('Failed to parse 409 error details:', parseError);
      }
    }

    callBacks.onError(error);
  };

  const extendedCallbacks = {
    ...callBacks,
    onError: extendedOnError,
  };

  return extendedCallbacks;
};

export const shapeCallbacks = (
  toast: (props: Omit<ToasterToast, 'id'>) => {
    id: string;
    dismiss: () => void;
    update: (props: ToasterToast) => void;
  },
  messages: Messages,
  mutation: MutationType,
  setIsSubmitting: (isSubmitting: boolean) => void,
  setShowDialog?: (showDialog: boolean) => void
) => {
  const callBacks = mutateCallbacks(
    toast,
    messages,
    mutation,
    'shape',
    setIsSubmitting,
    setShowDialog
  );

  const extendedOnError = async (error: any) => {
    const statusCode = error.response?.status;
    const errorData = await readErrorBody(error);

    if (statusCode === 400 && errorData?.message?.includes('self-crossings')) {
      toast({
        title: messages.genericToastTitleError,
        description: 'GeoJSON contains self-crossings',
        variant: 'destructive',
      });
      return;
    }

    callBacks.onError(error);
  };

  const extendedCallbacks = {
    ...callBacks,
    onError: extendedOnError,
  };

  return extendedCallbacks;
};

export const filterCallbacks = (
  toast: (props: Omit<ToasterToast, 'id'>) => {
    id: string;
    dismiss: () => void;
    update: (props: ToasterToast) => void;
  },
  messages: Messages,
  mutation: MutationType,
  setIsSubmitting: (isSubmitting: boolean) => void,
  reApplyFilters: () => void,
  setShowDialog?: (showDialog: boolean) => void
) => {
  const callBacks = mutateCallbacks(
    toast,
    messages,
    mutation,
    'filter',
    setIsSubmitting,
    setShowDialog
  );

  const extendedOnSuccess = async () => {};

  const extendedCallbacks = {
    ...callBacks,
    onSuccess: extendedOnSuccess,
  };

  return extendedCallbacks;
};
